import { observer } from "mobx-react-lite";
import { useContext } from "react";

import { AppointmentFormContext } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/context/AppointmentFormContext.ts";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { getSubmissionFormStyles } from "../appointment-dialog/components/appointment-form/AppointmentForm.styles.tsx";
import { AppointmentFormHelper } from "../appointment-dialog/components/appointment-form/context/AppointmentFormHelper.tsx";
import { SecondColumnFieldsSwitcher } from "../appointment-dialog/components/appointment-form/SecondColumnFieldsSwitcher.tsx";
import { AddToGroupApptFormFields } from "./components/AddToGroupApptFields.tsx";
import { AddToGroupApptDialogValidator } from "./validators/AddToGroupApptDialogValidator.ts";

const AddToGroupApptDialogBase: React.FunctionComponent = observer(() => {
  const { addToGroupApptSubmit, initialValues } = useContext(
    AppointmentFormContext
  );

  const { booking } = useStores();

  const validator = new AddToGroupApptDialogValidator();

  const onDismiss = () => {
    booking.ui.showCalendarEventDialog({
      showAddToGroupApptDialog: false
    });
  };

  const patientLabel = usePatientLabel();

  return (
    <SubmissionFormDialog<AppointmentFormValues>
      dialogName={`Add ${patientLabel} to group appointment`}
      dialogProps={{
        onDismiss,
        maxWidth: "auto",
        minWidth: "auto",
        dialogContentProps: {
          styles: {
            title: { display: "none" },
            inner: { padding: 0, height: "100%" },
            innerContent: { padding: 0 }
          }
        }
      }}
      onSubmit={addToGroupApptSubmit}
      onSubmitSucceeded={onDismiss}
      initialValues={initialValues}
      validate={validator.validate}
      styles={getSubmissionFormStyles(
        booking.ui.currentAppointment?.secondColumnContent
      )}
      sidePanel={<SecondColumnFieldsSwitcher />}
      hideButtons
    >
      {() => <AddToGroupApptFormFields onDismiss={onDismiss} />}
    </SubmissionFormDialog>
  );
});

export const AddToGroupApptDialog = observer(() => {
  const root = useStores();

  const hidden =
    !root.booking.ui.currentAppointment ||
    !root.booking.ui.currentAppointment.showAddToGroupApptDialog;

  if (hidden) return null;
  return (
    <DataFetcher fetch={({ booking }) => booking.loadAppointmentTypes()}>
      {appointmentTypes => {
        const groupApptTypes = appointmentTypes.filter(
          x => x.isGroupAppointmentType
        );

        const helper = new AppointmentFormHelper(
          root,
          groupApptTypes[0],
          undefined
        );

        return (
          <AppointmentFormContext.Provider value={helper}>
            <AddToGroupApptDialogBase />
          </AppointmentFormContext.Provider>
        );
      }}
    </DataFetcher>
  );
});
